import React, { useMemo } from 'react';
import cn from 'classnames';
import { Badge } from 'antd';
import { useHistory, useRouteMatch } from 'react-router-dom';
import type { StoryIssue } from 'common/building-blocks/link-capability';
import { validateStoryLinks } from 'common/building-blocks/link-capability';
import { generatePath } from 'common/utils/route';
import { MODAL } from 'admin/constants/common';
import { STORY_CARD_EDITOR_PAGE } from 'admin/constants/routes';
import { setModal } from 'admin/actions';
import { useAdminDispatch, useAdminSelector } from 'admin/reducers';
import { selectEditableStory } from 'admin/reducers/story-editor/selectors';
import { selectCurrentVersion } from 'admin/reducers/version-reducer';
import { selectCardEditor, selectEditableCardId, selectEditableStateInfo } from 'admin/reducers/card-editor/selectors';
import { getMergeOfEditableStoryWithCardEditor } from 'admin/actions/card-editor/card-editor-sync';
import CardEditorIframeTransmitter from 'admin/components/pages/Story/CardEditor/IframeTransmitter';
import * as clipboard from 'admin/utils/clipboard';
import { useStoryEditorChatState } from 'admin/components/pages/Story/StoryEditorChat/hooks/use-story-editor-chat-state';
import { Icon } from 'admin/components/common/Icon';
import Text from 'admin/components/common/Text';
import { getStoryIssuePresentation } from './presentation';
import css from './StoryIssues.scss';

export function getStoryIssuePrompt(issue: StoryIssue): string {
	return [
		'Fix this Storycards validation issue:',
		issue.message,
		`Code: ${issue.code}`,
		`Card: ${issue.cardId}`,
		`Element: ${issue.primaryElementId}`,
		...(issue.primaryNodeId ? [`Node: ${issue.primaryNodeId}`] : []),
	].join('\n');
}

export function useStoryIssues(): StoryIssue[] {
	const story = useAdminSelector(selectEditableStory);
	const version = useAdminSelector(selectCurrentVersion);
	const cardEditor = useAdminSelector(selectCardEditor);

	return useMemo(() => {
		if (!story?.storyVersions[version]) return [];
		const snapshot = getMergeOfEditableStoryWithCardEditor(story, cardEditor, version);
		return validateStoryLinks(snapshot.storyVersions[version].data);
	}, [cardEditor, story, version]);
}

export const StoryIssueList = ({
	issues,
	currentCardId,
	onSelect,
	onFix,
}: {
	issues: StoryIssue[];
	currentCardId?: string;
	onSelect?: (issue: StoryIssue) => void;
	onFix?: (issue: StoryIssue) => void;
}) => {
	if (issues.length === 0) {
		return (
			<Text size="label" className={css.empty}>
				No issues found.
			</Text>
		);
	}

	return (
		<ul className={css.list}>
			{issues.map(issue => {
				const presentation = getStoryIssuePresentation(issue);
				const showCard = !currentCardId || issue.cardId !== currentCardId;
				return (
					<li
						className={css.item}
						key={`${issue.code}-${issue.cardId}-${issue.primaryElementId}-${issue.relatedElementIds?.join('-') ?? ''}`}
					>
						<button type="button" className={css.issue} onClick={() => onSelect?.(issue)}>
							<span className={css.issueMeta}>
								<span className={css.severity} data-severity={issue.severity}>
									{presentation.severityLabel}
								</span>
								{showCard && <span className={css.cardName}>Card: {issue.cardName}</span>}
							</span>
							<strong className={css.issueTitle}>{presentation.title}</strong>
							<span className={css.issueDescription}>{presentation.description}</span>
							<span className={css.openHint}>
								{showCard ? `Open on ${issue.cardName}` : 'Select element'}
							</span>
						</button>
						<div className={css.actions}>
							<button
								type="button"
								onClick={() => void clipboard.copy({ text: getStoryIssuePrompt(issue) })}
							>
								Copy for chat
							</button>
							{onFix && (
								<button type="button" onClick={() => onFix(issue)}>
									Fix with AI
								</button>
							)}
						</div>
					</li>
				);
			})}
		</ul>
	);
};

export const StoryIssuesLauncher = ({ className }: { className?: string }) => {
	const dispatch = useAdminDispatch();
	const issues = useStoryIssues();
	const isOpen = useAdminSelector(state => (state.modal.id as MODAL) === MODAL.STORY_ISSUES);
	const title = `Review ${issues.length} story ${issues.length === 1 ? 'issue' : 'issues'}`;

	if (issues.length === 0) return null;

	return (
		<button
			type="button"
			title={title}
			aria-label={title}
			className={cn(css.launcher, className, { [css.active]: isOpen })}
			onClick={() => dispatch(setModal(isOpen ? undefined : { id: MODAL.STORY_ISSUES }))}
		>
			<Badge count={issues.length} size="small" overflowCount={99} offset={[0, 4]}>
				<Icon type="sc-alert" color="currentColor" width={18} />
			</Badge>
		</button>
	);
};

const StoryIssues = ({ open, close }: { open: boolean; close: () => void }) => {
	const history = useHistory();
	const cardEditorRoute = useRouteMatch({ path: STORY_CARD_EDITOR_PAGE, exact: true });
	const currentCardId = useAdminSelector(selectEditableCardId);
	const editableState = useAdminSelector(selectEditableStateInfo);
	const story = useAdminSelector(selectEditableStory)!;
	const issues = useStoryIssues();
	const { openWithDraft } = useStoryEditorChatState();

	const selectIssue = (issue: StoryIssue) => {
		if (!issue.primaryNodeId) return;
		close();
		if (cardEditorRoute && issue.cardId === currentCardId) {
			CardEditorIframeTransmitter.initiateElementSelectInClient({
				id: issue.primaryNodeId,
				editableState,
				eventOptions: {
					metaKey: true,
					ctrlKey: true,
				},
			});
			return;
		}

		history.push(generatePath(STORY_CARD_EDITOR_PAGE, { storyId: story.id, cardId: issue.cardId }));
	};

	return (
		<aside className={cn(css.panel, { [css.open]: open })} aria-label="Story issues">
			<div className={css.panelBody}>
				<div className={css.panelContainer}>
					<header className={css.header}>
						<Text size="subheading" weight="bold">
							Story issues
						</Text>
						<div className={css.headerActions}>
							<button type="button" className={css.close} aria-label="Close story issues" onClick={close}>
								<Icon type="close" width={16} color="currentColor" />
							</button>
						</div>
					</header>
					<StoryIssueList
						issues={issues}
						currentCardId={currentCardId}
						onSelect={selectIssue}
						onFix={issue => openWithDraft(getStoryIssuePrompt(issue))}
					/>
				</div>
			</div>
		</aside>
	);
};

export default StoryIssues;
